// The tabs on the Standings page: the same tournament read three ways.
//
//   standings   the ranked table — who is winning
//   crosstable  the same ranking laid out as games — who played whom
//   prizes      the same ranking cut into categories — who wins what
//
// All three are views of ONE ranking, the one js/ui/standings.js computes. This module
// owns nothing but the switch: which wrapper is on screen, which print button goes with
// it, and telling the active view to redraw when a result comes in. Each view registers
// itself, so adding a fourth means touching that view and nothing here.
//
// It exists because the alternative was the crosstable owning the prizes tab, or three
// modules each hiding the other two's containers and eventually disagreeing about which
// of them is showing.

// name -> { wrapId, printButtonId, render }
const views = new Map();

// Which view is on screen. Not persisted: the standings are what the page is for, and an
// arbiter who opened the crosstable once does not want it every time afterwards.
let active = 'standings';

/**
 * @param {string} name
 * @param {Object} def
 * @param {string} def.wrapId       the container to show for this view
 * @param {string[]} [def.buttonIds] the page-header buttons that belong to it — its print
 *                                   button, its editor, anything that acts on this view
 *                                   alone and has no meaning while another is showing
 * @param {Function} [def.render]   called when the view is shown, and on every refresh
 */
export function registerStandingsView(name, def) {
    views.set(name, { buttonIds: [], ...def });
}

// The standings table itself, registered here because js/ui/standings.js renders it on
// its own schedule and has no view of its own to draw.
registerStandingsView('standings', {
    wrapId: 'standings-table-wrap',
    buttonIds: ['btn-print-standings', 'btn-edit-tiebreaks', 'btn-fullscreen-standings', 'standings-view-columns']
});

export function activeStandingsView() {
    return active;
}

export function showStandingsView(name) {
    if (!views.has(name)) name = 'standings';
    const changed = name !== active;
    active = name;

    views.forEach((def, key) => {
        const wrap = document.getElementById(def.wrapId);
        if (wrap) wrap.style.display = key === active ? '' : 'none';
        (def.buttonIds || []).forEach(id => {
            const button = document.getElementById(id);
            // Only ever set to 'none' or cleared — never to a concrete value. Most of
            // these carry `arbiter-only`, which hides them with `display: none !important`
            // for a spectator, and clearing the inline style is what lets that rule keep
            // deciding rather than overriding it.
            if (button) button.style.display = key === active ? '' : 'none';
        });
    });

    // The tie-break guide explains the columns of the standings table, and those are only
    // on screen in that view.
    const footer = document.getElementById('standings-tiebreaks-footer');
    if (footer) footer.style.display = active === 'standings' ? '' : 'none';

    document.querySelectorAll('[data-standings-view]').forEach(btn => {
        btn.classList.toggle('active', btn.dataset.standingsView === active);
    });

    const def = views.get(active);
    if (def && typeof def.render === 'function') def.render();

    // On a wide screen the three share one scrolling card body (css/main.css), and the
    // tabs stay in view while it scrolls: without this the crosstable would open as far
    // down as the arbiter had read the standings.
    const body = changed && def && document.getElementById(def.wrapId)?.closest('.card-body');
    if (body) body.scrollTop = 0;
}

/**
 * Redraw whatever is on screen. Called from renderStandings(), which already runs on
 * every change that could move a player, so a result typed in while the crosstable or
 * the prize list is open reaches it too.
 */
export function refreshActiveStandingsView() {
    const def = views.get(active);
    if (def && typeof def.render === 'function') def.render();
}

export function setupStandingsViews() {
    document.querySelectorAll('[data-standings-view]').forEach(btn => {
        btn.addEventListener('click', () => showStandingsView(btn.dataset.standingsView));
    });
}
